import type { ListQuery, Page } from "@estelle/domain";

import type { Projection } from "@/modules/types";

/**
 * Every page of a query, projected.
 *
 * An export is of the filter, not of the visible page: someone who filters to
 * 300 orders and exports 25 has been handed the wrong answer without being
 * told. So this follows the cursor to the end.
 *
 * The cap is a guard, not a page size. A runaway filter — or a real API
 * returning far more than anyone meant to ask for — should stop at a number
 * a browser can hold rather than exhausting the tab. A real export of more
 * than this belongs to a server-side job, which is what the GST module's
 * XLSX export already is.
 */
export const MAX_ROWS = 10_000;
export const PAGE_SIZE = 200;

export async function collectAll<T>(
  fetch: (query: ListQuery) => Promise<Page<T>>,
  query: ListQuery,
  project: (row: T) => Projection,
): Promise<Projection[]> {
  const rows: Projection[] = [];
  let cursor: string | undefined = query.cursor;

  while (rows.length < MAX_ROWS) {
    const page: Page<T> = await fetch({
      ...query,
      limit: PAGE_SIZE,
      cursor,
    });

    for (const item of page.items) rows.push(project(item));

    if (!page.nextCursor || page.items.length === 0) break;
    cursor = page.nextCursor;
  }

  return rows;
}
